/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ComponentActionsBar } from "cms/models/layout/model-components-box/component-actions-bar";
import { ComponentFilterBar } from "cms/models/layout/model-components-box/component-filter-bar";
import { ComponentList } from "cms/models/layout/model-components-box/component-list";
import { EmptyComponentList } from "cms/models/layout/model-components-box/empty-component-list";
import { filterComponentsByCategory } from "cms/components/components/component-utils";
import { filterComponentsBySearchTerm } from "cms/components/components/component-utils";
import { sortComponentsAtoZ } from "cms/components/components/component-utils";
import { sortComponentsZtoA } from "cms/components/components/component-utils";
import { useComponentsWithCategories } from "cms/models/hooks/use-components-with-categories";
import { ActiveFieldBar } from "cms/models/layout/model-components-box/active-field-bar";
import { selectAllFieldSchemas } from "data/store/reducers/schema-model";
import { selectComposerActiveFieldName } from "data/store/reducers/composer";
import { setActiveFieldName } from "data/store/reducers/composer";
import { setFieldOpen } from "data/store/reducers/composer";
import { ReuseComponent } from "cms/components/layout/reuse/find-component";
import { useDispatch } from "react-redux";
import { useFieldOpen } from "cms/fields/hooks/use-field-open";

const ModelComponentsBox = (props) => {

    const componentPosition = props?.componentPosition;
    const dialogFieldName = props?.dialogFieldName;
    const fieldRefs = props?.fieldRefs;
    const handleCloseModal = props?.handleCloseModal;
    const showActiveField = props?.showActiveField;
    
    const componentTypeField = "field.component";

    const componentTypes = useComponentsWithCategories();
    const allFields = useSelector(selectAllFieldSchemas);
    const activeFieldOptions =  allFields?.filter((field) => {
        return componentTypeField === field.type;
    });
    const hasActiveFieldOptions = activeFieldOptions && activeFieldOptions.length > 0;
    const { getNamedField } = useFieldOpen(name);
    
    const [searchTerm, setSearchTerm] = useState("");
    const [isSortedAtoZ, setIsSortedAtoZ] = useState(true);
    const [isFilterBarShown, setIsFilterBarShown] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("");
    const activeFieldName = useSelector(selectComposerActiveFieldName);
    const dispatch = useDispatch();

    const filterComponents = () => {
        let componentsFilteredByCategory = componentTypes;
        if (selectedFilter.length > 1) {
            componentsFilteredByCategory = filterComponentsByCategory(componentTypes, selectedFilter);
        }

        let filteredComponents = componentsFilteredByCategory;
        if (searchTerm.length > 1) {
            filteredComponents = filterComponentsBySearchTerm(componentsFilteredByCategory, searchTerm);
        }
        return filteredComponents;
    };

    const sortComponents = (components) => {
        if (isSortedAtoZ) {
            return sortComponentsAtoZ(components);
        } else {
            return sortComponentsZtoA(components);
        }
    };

    const handleSortChange = () => {
        setIsSortedAtoZ((prevState) => !prevState);
    };

    const filteredAndSortedComponents = useMemo(() => {
        let filteredComponents = filterComponents();
        return sortComponents(filteredComponents);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSortedAtoZ, searchTerm, selectedFilter]);

    const showFilterBar = (showBar) => {
        setIsFilterBarShown(showBar);
    };

    const handleSelectField = (event) => {
        const selectedFieldName = event.target.value;
        dispatch(setActiveFieldName(selectedFieldName));
        const field = getNamedField(selectedFieldName);
        dispatch(setFieldOpen(field));
        scrollToField(selectedFieldName);
    };

    const scrollToField = (fieldName) => {
        const ref = fieldRefs.current?.get(fieldName);
        ref?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className='model-components-box'>
            <div className='action-bar-container'>
                <ComponentActionsBar
                    handleSortChange={handleSortChange}
                    isDisabled={!hasActiveFieldOptions}
                    isFilterBarShown={isFilterBarShown}
                    isSortedAtoZ={isSortedAtoZ}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showFilterBar={showFilterBar}
                />
                {isFilterBarShown &&
                    <ComponentFilterBar
                        componentTypes={componentTypes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        showFilterBar={showFilterBar}
                    />
                }
            </div>
            {showActiveField &&
                <ActiveFieldBar
                    activeFieldName={activeFieldName}
                    activeFieldOptions={activeFieldOptions}
                    handleSelectField={handleSelectField}
                    isDisabled={!hasActiveFieldOptions}
                />
            }
            {hasActiveFieldOptions &&
                <ComponentList
                    activeFieldName={activeFieldName}
                    componentPosition={componentPosition}
                    components={filteredAndSortedComponents}
                    dialogFieldName={dialogFieldName}
                    handleCloseModal={handleCloseModal}
                    isDisabled={!hasActiveFieldOptions}
                />
            }
            {!hasActiveFieldOptions &&
                <EmptyComponentList />
            }
            <ReuseComponent
                closeAddComponentModal={handleCloseModal}
                componentPosition={componentPosition}
                fieldName={dialogFieldName || activeFieldName}
                isDisabled={!hasActiveFieldOptions}
            />
        </div>
    );
};

export { ModelComponentsBox };
